import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  Grid,
  MenuItem,
  InputLabel,
  FormControl,
  Select,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import API_BASE_URL from '../config';
import Seo from '../components/Seo';
import { trackLead, trackEvent } from '../utils/analytics';

const Careers = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    education: '',
    resume: null,
    coverLetter: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const positions = [
    'Marine Inspector',
    'Cargo Surveyor',
    'Quality Control Inspector',
    'Project Cargo Specialist',
    'Technical Supervisor',
    'Operations Manager',
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFormData(prevState => ({
      ...prevState,
      resume: e.target.files[0]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('fullName', formData.fullName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('position', formData.position);
      formDataToSend.append('experience', formData.experience);
      formDataToSend.append('education', formData.education);
      formDataToSend.append('coverLetter', formData.coverLetter);
      
      // Append file if selected
      if (formData.resume) {
        formDataToSend.append('resume', formData.resume);
      }

      // Debug: Log the form data being sent
      console.log('Form data being sent:', {
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        position: formData.position,
        experience: formData.experience,
        education: formData.education,
        coverLetter: formData.coverLetter,
        resume: formData.resume ? formData.resume.name : 'No file'
      });

      const response = await fetch(`${API_BASE_URL}/careers.php`, {
        method: 'POST',
        body: formDataToSend, // Send FormData instead of JSON
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      const result = await response.json();

      // Debug: Log the response
      console.log('API Response:', result);

      if (result.success) {
        setSnackbarMessage(result.message);
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
        trackLead('careers', { position: formData.position });
        
        // Reset form after successful submission
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          position: '',
          experience: '',
          education: '',
          resume: null,
          coverLetter: '',
        });
      } else {
        setSnackbarMessage(result.message || 'Error submitting application. Please try again.');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        trackEvent('form_error', { form: 'careers' });
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      let errorMessage = 'Network error. Please check your connection and try again.';
      
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        errorMessage = 'Unable to connect to server. Please check your internet connection.';
      } else if (error.name === 'SyntaxError') {
        errorMessage = 'Invalid response from server. Please try again.';
      }
      
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <Box sx={{ py: 8 }}>
      <Seo
        title="Careers — Join Our Inspection Team"
        description="Explore careers at InspectIQ. We're hiring marine inspectors, cargo surveyors and quality control professionals. Apply online with your resume."
        path="/careers"
      />
      <Container maxWidth="lg">
        <Typography variant="h2" align="center" gutterBottom>
          Join Our Team
        </Typography>
        <Typography variant="h5" align="center" color="text.secondary" sx={{ mb: 6 }}>
          Be part of our mission to deliver excellence in inspection services
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 4, height: '100%' }}>
              <Typography variant="h4" gutterBottom>
                Why Work With Us?
              </Typography>
              <Typography paragraph>
                At InspectIQ, we believe in fostering talent and providing opportunities for growth.
                Join us to be part of an innovative team that values:
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                {[
                  'Professional Development',
                  'Collaborative Environment',
                  'Global Exposure',
                  'Competitive Benefits',
                  'Work-Life Balance',
                  'Career Advancement'
                ].map((benefit, index) => (
                  <Typography component="li" key={index} sx={{ mb: 1 }}>
                    {benefit}
                  </Typography>
                ))}
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ p: 4 }}>
              <Typography variant="h4" gutterBottom>
                Application Form
              </Typography>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Position Applied For</InputLabel>
                      <Select
                        name="position"
                        value={formData.position}
                        onChange={handleChange}
                        label="Position Applied For"
                      >
                        {positions.map((pos) => (
                          <MenuItem key={pos} value={pos}>
                            {pos}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Years of Experience"
                      name="experience"
                      type="number"
                      value={formData.experience}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Highest Education"
                      name="education"
                      value={formData.education}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      startIcon={<CloudUploadIcon />}
                      sx={{ 
                        mb: 2,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      {formData.resume ? formData.resume.name : 'Upload Resume (PDF, DOC)'}
                      <input
                        type="file"
                        hidden
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                        required
                      />
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Cover Letter"
                      name="coverLetter"
                      multiline
                      rows={4}
                      value={formData.coverLetter}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      disabled={isSubmitting}
                      sx={{ mt: 2 }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Submit Application'
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Card>
          </Grid>
        </Grid>
      </Container>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Careers;
